import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@ohpen/react-auth';
import { getCdnAssets, useConfig, type CdnAssets } from '@ohpen/react-utils';
import type { TopBarProps } from '@ohpen/enso-ui';

import { ConfigProvider } from '@config/context';
import { ConfigLayout } from '@config/shell';
import type { ConfigAppProps, ConfigContextValue } from '@config/interfaces';
import { resolveReturnTo } from '@config/utils';

/**
 * Entry point of the configuration module. Everything it needs comes from the shared
 * libraries or from its own props, never from the application hosting it.
 *
 * The context is explicit rather than inferred by any screen: the application arrives as a
 * prop from the host, the client and the environment from this deployment's configuration.
 * None of them is in the path, because none of them can vary within one deployment.
 */
export const ConfigApp = ({
  application,
  applicationName,
  client,
  customMenu,
  onClickMyAccount,
  aboutInfo,
  configuration,
  getValueLists,
  permissionCatalogue,
  roleCatalogue,
  flagDescriptions,
  customFields,
  getParameters,
  getUsers,
  canViewSection,
  returnToOrigins,
}: ConfigAppProps): ReactElement => {
  const config = useConfig();
  // The client the host is previewing, which is what the theme and the logo follow.
  const themedConfig = useMemo(() => ({ ...config, CLIENT: client ?? config.CLIENT }), [client, config]);
  const auth = useAuth();
  const [searchParams] = useSearchParams();
  const [cdnAssets, setCdnAssets] = useState<CdnAssets | null>(null);

  useEffect(() => {
    getCdnAssets(themedConfig).then(assets => {
      if (assets) setCdnAssets(assets);
    });
  }, [themedConfig]);

  const value: ConfigContextValue = useMemo(
    () => ({
      application,
      applicationName,
      client: themedConfig.CLIENT,
      environment: config.STAGE,
      returnTo: resolveReturnTo(searchParams.get('returnTo'), returnToOrigins),
      configuration,
      getValueLists,
      permissionCatalogue,
      roleCatalogue,
      flagDescriptions,
      customFields,
      getParameters,
      getUsers,
      canViewSection,
    }),
    [
      application,
      applicationName,
      themedConfig.CLIENT,
      config.STAGE,
      configuration,
      getValueLists,
      permissionCatalogue,
      roleCatalogue,
      flagDescriptions,
      customFields,
      getParameters,
      getUsers,
      canViewSection,
      returnToOrigins,
      searchParams,
    ],
  );

  const userMenu: TopBarProps['userMenu'] = useMemo(
    () => ({ name: auth.userDisplayName, onClickLogout: () => auth.logout(), aboutInfo }),
    [aboutInfo, auth],
  );

  return (
    <ConfigProvider value={value}>
      <ConfigLayout
        logoSrc={cdnAssets?.logoHorizontalPath ?? ''}
        userMenu={userMenu}
        customMenu={customMenu}
        onClickMyAccount={onClickMyAccount}
      />
    </ConfigProvider>
  );
};
